import { Injectable, Inject } from '@angular/core';
import { IUser } from '../models/account/user.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { UserLogin } from '../models/account/user-login.model';
import { LoginResponse } from '../models/account/login-response.model';
import { LocalStorageService } from './local-storage.service';
import { Const } from './Const';
import { AlertService, MessageSeverity } from './alert.service';

import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, AccountInfo } from '@azure/msal-browser';

@Injectable()
export class AuthService {
  currentUser: IUser;

  private currentUserSubject = new BehaviorSubject<IUser | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  readonly isAuthenticated$: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private alertService: AlertService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.isAuthenticated$ = combineLatest([
      this.msalBroadcastService.inProgress$,
      this.msalBroadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None),
        map(() => !!(this.getOrSetActiveAccount()))
      )
    ]).pipe(
      map(([status, hasAccount]) => status === InteractionStatus.None && hasAccount),
      distinctUntilChanged()
    );

    this.msalBroadcastService.inProgress$
      .pipe(filter(status => status === InteractionStatus.None))
      .subscribe(() => {
        const account = this.getOrSetActiveAccount();
        const user = account ? this.mapAccountToUser(account) : this.emptyUser();
        this.currentUser = user;
        this.currentUserSubject.next(user);
        if (user?.userName) {
          this.localStorageService.saveData(Const.CurrentUser, user);
        }
      });
    if (this.msalService.instance.getActiveAccount() != null) {
      this.getLoginEndpoint<LoginResponse>(this.msalService.instance.getActiveAccount().idTokenClaims.oid, this.msalService.instance.getActiveAccount().idTokenClaims.name, this.msalService.instance.getActiveAccount().idTokenClaims.preferred_username)
        .subscribe();
    }
    else
      this.getLogOffEndpoint().subscribe();

  }

  loginUser(): Observable<AuthenticationResult> {
    return this.msalService.loginPopup({ ...(this.msalGuardConfig.authRequest as PopupRequest) });
  }

  logOff() {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      this.getLogOffEndpoint().subscribe({
        complete: () => {
          this.msalService.logout().subscribe({
            complete: () => {
              const name = this.currentUser?.userName ?? account.username;
              this.alertService.showMessage('Logout', `${name} logged out`, MessageSeverity.information);
              this.localStorageService.removeData(Const.CurrentUser);
              this.currentUser = this.emptyUser();
              this.currentUserSubject.next(this.currentUser);
              this.getLogOffEndpoint().subscribe();
            }
          });
        }
      });
    }
  }

  getLoginEndpoint<LoginResponse>(userid: string, userName: string, email: string): Observable<LoginResponse> {
    const params = new HttpParams()
      .append('userid', userid)
      .append('username', userName)
      .append('email', email);

    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) };
    return this.http.post<LoginResponse>('/account/login', params.toString(), options);
  }

  processLoginResponse(response: LoginResponse, rememberMe?: boolean) {
    this.currentUser = {
      userName: response.userName,
      password: response.password,
      roles: [],
      email: response.email,
      userImage: '/UserImages/' + response.userName + '.png'
    };
    this.localStorageService.saveData(Const.CurrentUser, this.currentUser);
    this.currentUserSubject.next(this.currentUser);
    return this.currentUser;
  }

  private getLogOffEndpoint() {
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) };
    return this.http.post('/account/LogOff', null, options);
  }

  isAuthenticated(): boolean {
    const account = this.getOrSetActiveAccount();
    this.currentUser = account ? this.mapAccountToUser(account) : this.emptyUser();
    this.currentUserSubject.next(this.currentUser);
    return !!account;
  }

  private getOrSetActiveAccount(): AccountInfo | null {
    const active = this.msalService.instance.getActiveAccount();
    if (active) return active;
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts?.length) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
    return null;
  }

  private mapAccountToUser(account: AccountInfo): IUser {
    const username = account.username ?? account.homeAccountId ?? '';
    return {
      userName: username,
      password: '',
      roles: [],
      email: username,
      userImage: '/UserImages/' + username + '.png'
    };
  }

  private emptyUser(): IUser {
    return { userName: '', password: '', roles: [], email: '', userImage: '' };
  }
}
